import { classnames } from '@aibees/shared/utils';
import { Typography, useTheme } from '@mui/material';
import { cloneElement, JSX, memo, useEffect, useMemo } from 'react';
import { Scrollbar } from '../scrollbar';
import * as Styled from './styles';
import type { CustomDrawerProps as Props } from './types';

const DRAWER_SLOT_PROPS = {
  backdrop: { invisible: false },
} as const;

const SCROLLBAR_CLASS_NAMES = {
  contentEl: 'DrawerContentScrollBar_Content',
} as const;

export const CustomDrawer = memo(
  ({
    className,
    state,
    width,
    children,
    contentWrapperSx,
    content,
    icon,
    title,
    titleSx,
    position,
    defaultOpen: _defaultOpen,
    uncontrolledElement,
    onOpen,
    onClose,
    ModalProps: ModalPropsProp,
    hideBackdrop,
    ...other
  }: Props): JSX.Element => {
    const theme = useTheme();
    const open = state;
    const resolvedContent = children ?? content;

    useEffect(() => {
      if (open?.value) {
        document.body.style.overflow = 'auto';
        onOpen?.();
      } else {
        document.body.style.overflow = '';
        onClose?.();
      }
    }, [open?.value, onOpen, onClose]);

    const triggerElement = useMemo(() => {
      if (!uncontrolledElement) return null;
      return cloneElement(uncontrolledElement, {
        ...uncontrolledElement.props,
        onClick: open?.onToggle,
      });
    }, [uncontrolledElement, open?.onToggle]);

    const modalProps = useMemo(
      () => ({
        disableScrollLock: true,
        keepMounted: true,
        hideBackdrop: hideBackdrop ?? true,
        ...(ModalPropsProp ?? {}),
        onBackdropClick: ModalPropsProp?.onBackdropClick ?? open?.onFalse,
      }),
      [hideBackdrop, ModalPropsProp, open?.onFalse]
    );

    const paperProps = useMemo(
      () => ({
        sx: {
          width: 1,
          maxWidth: width ?? 420,
          minHeight: 1,
          zIndex: 1000,
        },
      }),
      [width]
    );

    const scrollbarSx = useMemo(
      () => ({
        height: 1,
        '& .simplebar-content-wrapper': {
          paddingBottom: theme.spacing(2),
        },
      }),
      [theme]
    );

    const titleSxMerged = useMemo(
      () => ({ flexGrow: 1, ...titleSx }),
      [titleSx]
    );

    return (
      <>
        {triggerElement}

        <Styled.Wrapper
          className={classnames('CustomDrawer', className)}
          open={open?.value}
          {...other}
          anchor={position ?? 'right'}
          slotProps={DRAWER_SLOT_PROPS}
          ModalProps={modalProps}
          PaperProps={paperProps}
        >
          <Scrollbar
            className="DrawerContentScrollBar"
            classNames={SCROLLBAR_CLASS_NAMES}
            sx={scrollbarSx}
          >
            {title != null && (
              <Styled.Header>
                {icon != null && (
                  <Styled.HeaderIcon onClick={onClose} name={icon} />
                )}
                <Typography variant="body2" sx={titleSxMerged}>
                  {title}
                </Typography>
              </Styled.Header>
            )}

            {resolvedContent != null && (
              <Styled.Content sx={contentWrapperSx}>
                {resolvedContent}
              </Styled.Content>
            )}
          </Scrollbar>
        </Styled.Wrapper>
      </>
    );
  }
);

CustomDrawer.displayName = 'CustomDrawer';
